// ─────────────────────────────────────────────────────────────
// PrintButton.jsx — Noble Enterprises (tape module) print engine
// Renders a button that builds and outputs a PDF from a config.
//
// Usage:
//   <PrintButton config={poPrintConfig(po)} filename={po.po_no} />
//   <PrintButton getConfig={() => buildStockConfig(rows)} label="Stock Report" />
// ─────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { saveDoc, printDoc } from './buildDoc';

const PrintButton = ({
  config,           // config object, OR
  getConfig,        // () => config  /  async () => config   (preferred for lists)
  filename = 'report',
  mode = 'save',    // 'save' | 'print'
  label = 'Print',
  icon = '🖨',
  disabled = false,
  variant = 'default',   // 'default' | 'ghost' | 'small'
  style = {},
  onError,
}) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const cfg = getConfig ? await getConfig() : config;
      if (!cfg) {
        setBusy(false);
        return;
      }
      if (mode === 'print') printDoc(cfg);
      else saveDoc(cfg, typeof filename === 'function' ? filename() : filename);
    } catch (err) {
      console.error('PrintButton failed:', err);
      if (onError) onError(err);
      else alert('Could not generate the PDF. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    background: '#fff',
    color: '#1a3c6e',
    fontWeight: 600,
    cursor: disabled || busy ? 'not-allowed' : 'pointer',
    opacity: disabled || busy ? 0.6 : 1,
    transition: 'background 0.15s, border-color 0.15s',
  };

  const variants = {
    default: { padding: '7px 14px', fontSize: 13 },
    small: { padding: '4px 9px', fontSize: 12 },
    ghost: {
      padding: '4px 8px',
      fontSize: 12,
      border: 'none',
      background: 'transparent',
    },
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || busy}
      title={label}
      style={{ ...base, ...(variants[variant] || variants.default), ...style }}
    >
      <span aria-hidden="true">{busy ? '⏳' : icon}</span>
      {variant !== 'ghost' || label ? <span>{busy ? 'Generating…' : label}</span> : null}
    </button>
  );
};

export default PrintButton;